import type { AnalyzedRow, StudentImportAnalysis } from '@repo/shared-types';
import type { ImportCommitInput } from '@repo/validation';

/** THI-267 Task 13 — what `AddMembersDialog`'s "Nhập từ Excel" tab passes to
 *  `ImportWizard` to run it embedded, with the class as context (spec §5's
 *  AddMembersDialog bullet): no môn/giá step (the wizard never had one — see
 *  `MappingStep.tsx`), a price column in Xem trước, "Dùng học viên đã có" on
 *  dup rows, and Xem trước IS the confirm step (no second confirmation).
 *
 *  `startDate`/`pricing` are FIXED here, not asked in the wizard — the S6/3
 *  frame carries no date/hình-thức picker, just the subtitle's "học phí mặc
 *  định …". `startDate` is always today (the caller resolves it); `pricing`
 *  is the class's own defaults, falling back to Theo buổi (decision 14's
 *  reversed default) when the class has none set. */
export interface ImportClassContext {
  classId: string;
  className: string;
  subjectName: string;
  startDate: string;
  pricing: NonNullable<ImportCommitInput['pricing']>;
  /** The class default rate for `pricing.pricingType`, else the subject
   *  default — prefills (as a placeholder, not a stored value) the Xem
   *  trước Học phí column. Null means "no default; the operator must type
   *  something or the row 400s RATE_REQUIRED at commit". */
  defaultRate: number | null;
}

// Local (client-only) per-row edit state for the preview step, keyed by
// AnalyzedRow.rowNum. Seeded from the analysis and mutated as the user fixes
// needs_fix rows or decides to keep/skip dup_warning rows.
export interface RowEditState {
  studentName: string;
  payerName: string;
  payerPhone: string;
  /** needs_fix only: user chose "bỏ" — exclude this row from the commit set
   * even if they've fixed the fields. */
  excluded: boolean;
  /** Plain import (no classId) dup_warning only: user chose to keep this row
   * despite the duplicate warning. Defaults to false (skip). Unused in a
   * class-context commit — see `useExisting` below, which replaces this
   * choice with "which of the two ways to include it". */
  kept: boolean;
  /** Class-context only. Xem trước's editable Học phí cell — raw text, ''
   *  falls back to the class/subject default (never sent as 0). Unused
   *  (and never rendered) without a `classContext`. */
  rate: string;
  /** Class-context dup_warning rows only: "Dùng học viên đã có" (default,
   *  true) vs "Tạo mới". A true row sends `useExistingStudentId` (from
   *  `AnalyzedRow.existingStudentId`) and skips student/payer creation
   *  entirely; false imports it as a brand-new student like any other row. */
  useExisting: boolean;
}

/**
 * The single source of truth for "which analyzed rows will actually be
 * committed" — used both to build the commit payload and to count rows for
 * the result screen, so the two can never diverge.
 *
 * Plain import (classContext null) — unchanged from before Task 13:
 *  - ready/warning: always (backend guarantees a payer name);
 *  - dup_warning: only if the user kept it;
 *  - needs_fix: only if not excluded AND both studentName and payer name are
 *    non-empty after editing (the commit schema requires payer.name).
 *
 * Class context — a dup_warning row is ALWAYS included (decision 8: it
 * either resolves to the existing student via `useExisting`, or imports as
 * new; there is no third "skip" state in this tab, unlike the plain
 * import's kept/not-kept binary), and a dup_warning row resolving to the
 * existing student does not need a payer name at all (the server ignores
 * `payer`/`studentName` once `useExistingStudentId` is set) — so the
 * studentName/payerName non-empty check is skipped for it.
 */
/** Fix wave (review C2) — a `dup_warning` row with `existingStudentId ===
 *  null` has no DB row to link to: `classifyRows.ts` only reaches this
 *  combination via `seenInBatch`, i.e. a SECOND occurrence of the same
 *  name+phone within the FILE ITSELF, not an existing org student. Before
 *  this fix `resolvesToExisting` was false for it (correctly — there is no
 *  student to link), but nothing then stopped it falling through to a plain
 *  new-student commit while its own Select still read "Dùng học viên đã
 *  có" — the exact case decision 8 exists to prevent, and the ordinary
 *  shape of a cohort roster that names each child once per khung giờ. */
export function isBatchDuplicateRow(row: AnalyzedRow, classContext: ImportClassContext | null): boolean {
  return classContext !== null && row.status === 'dup_warning' && row.existingStudentId === null;
}

export function includedRows(
  analysis: StudentImportAnalysis,
  rowEdits: Record<number, RowEditState>,
  classContext: ImportClassContext | null = null,
): AnalyzedRow[] {
  const rows: AnalyzedRow[] = [];
  for (const row of analysis.rows) {
    const edit = rowEdits[row.rowNum];
    if (!edit) continue;
    if (row.status === 'needs_fix' && edit.excluded) continue;

    // A same-batch duplicate defaults to "bỏ qua" (edit.useExisting stays
    // true, its seeded default) — the FIRST occurrence in the file already
    // carries the student; only an explicit "Tạo mới" (useExisting flipped
    // to false) imports this row as a second, separate profile.
    if (isBatchDuplicateRow(row, classContext) && edit.useExisting) continue;

    const resolvesToExisting = classContext !== null && row.status === 'dup_warning' && edit.useExisting && row.existingStudentId !== null;
    if (resolvesToExisting) { rows.push(row); continue; }

    if (row.status === 'dup_warning' && classContext === null && !edit.kept) continue;

    const studentName = edit.studentName.trim();
    const payerName = edit.payerName.trim();
    if (!studentName || !payerName) continue;

    rows.push(row);
  }
  return rows;
}

/**
 * Derives the exact commit payload's `students[]` from the analysis + the
 * user's inline edits, so the displayed "will import N" (via `includedRows`)
 * can never diverge from what's actually committed. In class context, also
 * threads `rate` (parsed, blank → omitted so the server falls back to the
 * class/subject default) and `useExistingStudentId` (a resolved dup_warning
 * row only) per student — `commitStudentImport` hands these straight to
 * `addClassMembers`'s own per-row logic (Task 7).
 */
export function buildCommitRows(
  analysis: StudentImportAnalysis,
  rowEdits: Record<number, RowEditState>,
  classContext: ImportClassContext | null = null,
): ImportCommitInput['students'] {
  return includedRows(analysis, rowEdits, classContext).map((row) => {
    const edit = rowEdits[row.rowNum]!;
    const resolvesToExisting = classContext !== null && row.status === 'dup_warning' && edit.useExisting && row.existingStudentId !== null;

    const base = {
      // A resolved-to-existing row's name/payer are ignored server-side once
      // useExistingStudentId is set, but the schema still requires non-empty
      // strings — fall back to whatever the row already carries (the
      // existing student's own name/payer, or the mapped file value) rather
      // than block the row on a technicality nobody will read.
      studentName: (edit.studentName.trim() || row.mapped.studentName || row.existingPayer?.name || '—'),
      nickname: row.mapped.nickname,
      dateOfBirth: row.mapped.dateOfBirth,
      tags: row.mapped.tags,
      // Fix wave (review M25) — the `row.existingPayer` fallback is a
      // class-context concept (a resolved dup_warning row's matched
      // profile); it used to run unconditionally here, before the
      // `classContext === null` early return below, so the plain import
      // path's payload could read a class-context field it never asked
      // for. Near no-op in practice (a dup_warning match keys on
      // name+phone, so the two already agree) but unintended.
      payer: {
        name: edit.payerName.trim() || (classContext !== null ? row.existingPayer?.name : undefined) || row.mapped.payerName || '—',
        phone: edit.payerPhone.trim() || (classContext !== null ? row.existingPayer?.phone : undefined) || null,
        email: row.mapped.payerEmail,
      },
    };

    if (classContext === null) return base;

    const rate = parseClassRate(edit.rate);
    return {
      ...base,
      ...(rate !== undefined ? { rate } : {}),
      ...(resolvesToExisting ? { useExistingStudentId: row.existingStudentId! } : {}),
    };
  });
}

/** Fix wave (review C2 copy) — best-effort CLIENT reconstruction of which
 *  earlier row a same-batch duplicate matches, for the "Trùng với dòng N
 *  trong file" note. The server's own `seenInBatch` key (`classifyRows.ts`,
 *  student name + payer phone, both normalized) never reaches the wire, so
 *  this mirrors it approximately from what IS on `AnalyzedRow.mapped` —
 *  display-only, never the commit decision (that stays server-side via
 *  `existingStudentId`/`status`). */
export function findBatchDuplicateOf(rows: AnalyzedRow[]): Map<number, number> {
  const seen = new Map<string, number>();
  const dupOf = new Map<number, number>();
  for (const row of rows) {
    if (!row.mapped.studentName) continue;
    const key = `${row.mapped.studentName.trim().toLowerCase().replace(/\s+/g, ' ')}|${(row.mapped.payerPhone ?? '').replace(/\D/g, '')}`;
    const first = seen.get(key);
    if (first !== undefined && row.status === 'dup_warning' && row.existingStudentId === null) {
      dupOf.set(row.rowNum, first);
    }
    if (first === undefined) seen.set(key, row.rowNum);
  }
  return dupOf;
}

/** Mirrors `parseRateInput` (`pages/classes/addMembers/confirmRows.ts`) —
 * duplicated rather than imported: that module lives under `pages/classes`
 * and this one under `pages/students`, and the two wizards' commit shapes
 * are different enough (this one is a per-student field on a bigger commit
 * payload, not a batch-add row) that sharing the function would mean
 * sharing a directory neither domain owns. Same behaviour: blank/zero/
 * unparseable → undefined (falls back to the class/subject default), never
 * a 0 the server would reject with RATE_REQUIRED. */
function parseClassRate(raw: string): number | undefined {
  const digits = raw.trim().replace(/[^\d]/g, '');
  if (!digits) return undefined;
  const n = Number(digits);
  return n > 0 ? n : undefined;
}
